import { authHeaders } from './impersonation';
import type { ApiRequest } from '../../shared/src/utils/httpClients';

/**
 * A signed-in API call. A 401 signs the page out everywhere; a response that arrives after the
 * account changed in another tab is dropped.
 */
export async function apiRequest<T>(
  token: string,
  path: string,
  { method = 'GET', body, signal }: { method?: string; body?: unknown; signal?: AbortSignal } = {},
  failure = 'Please try again.'
): Promise<T> {
  const response = await fetch(path, {
    method,
    signal,
    headers: {
      ...authHeaders(token),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (localStorage.getItem('soulthread.session') !== token) throw new Error('Account changed');
  if (response.status === 401) {
    window.dispatchEvent(new Event('soulthread:session-expired'));
    throw new Error('Your session has expired. Please sign in again.');
  }
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(error?.error || failure);
  }
  return response.json() as Promise<T>;
}

/** API calls signed with this session, for the clients the web shares with the phone. */
export const webRequest =
  (token: string): ApiRequest =>
  (path, init, failure) =>
    apiRequest(token, path, init, failure);
